import React, { useState } from 'react'
import { message } from 'antd'
import { useNavigate } from 'react-router-dom'
import Load from '../../Load/Load'
import { FaArrowLeft } from 'react-icons/fa'

const AddStoryPage = () => {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)

    const [formData, setFormData] = useState({
        storyDescription: '',
        storyLength: 'medium',
        genre: 'general',
        style: 'narrative',
        learningLevel: 'intermediate'
    })

    const storyLengths = [
        { value: 'short', label: 'Short (100-200 words)' },
        { value: 'medium', label: 'Medium (300-400 words)' },
        { value: 'long', label: 'Long (400-700 words)' }
    ]

    const genres = [
        { value: 'general', label: 'General' },
        { value: 'fantasy', label: 'Fantasy' },
        { value: 'sci-fi', label: 'Science Fiction' },
        { value: 'mystery', label: 'Mystery' },
        { value: 'romance', label: 'Romance' },
        { value: 'adventure', label: 'Adventure' },
        { value: 'horror', label: 'Horror' },
        { value: 'drama', label: 'Drama' },
        { value: 'comedy', label: 'Comedy' }
    ]

    const styles = [
        { value: 'narrative', label: 'Narrative' },
        { value: 'descriptive', label: 'Descriptive' },
        { value: 'dialogue-heavy', label: 'Dialogue Heavy' },
        { value: 'poetic', label: 'Poetic' },
        { value: 'journalistic', label: 'Journalistic' }
    ]

    const learningLevels = [
        { value: 'starter', label: 'Starter' },
        { value: 'beginner', label: 'Beginner' },
        { value: 'intermediate', label: 'Intermediate' },
        { value: 'fluent', label: 'Fluent' }
    ]

    const handleInputChange = (e) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        // Validate required fields
        if (!formData.storyDescription.trim()) {
            message.error("Please enter a story description")
            return
        }

        if (formData.storyDescription.trim().length < 10) {
            message.error("Story description must be at least 10 characters long")
            return
        }

        try {
            setLoading(true)

            // Note: This would typically call an API to generate a story
            // For now, we'll show a message that this feature would be implemented
            message.info("Story generation feature would be implemented here. This requires backend AI integration.")

            // In a real implementation, you would call something like:
            // const response = await generateStory(formData)
            // if (response?.data?.success) {
            //     message.success("Story generated successfully")
            //     navigate('/admin/stories')
            // }

        } catch (error) {
            console.log("Error while generating story", error.message)
            message.error(error?.response?.data?.message || "Error while generating story")
        } finally {
            setLoading(false)
        }
    }

    const handleGoBack = () => {
        navigate('/admin/stories')
    }

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-4">
                        <button
                            onClick={handleGoBack}
                            className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition duration-200"
                        >
                            <FaArrowLeft className="mr-2" />
                            Back to Stories
                        </button>
                        <h1 className="text-3xl font-bold text-gray-800">Generate New Story</h1>
                    </div>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                    <h3 className="text-lg font-medium text-blue-800 mb-2">AI Story Generation</h3>
                    <p className="text-blue-700">
                        This feature uses AI to generate custom stories based on your description and preferences.
                        The story will be tailored to the specified length, genre, style, and learning level.
                    </p>
                </div>

                {/* Main Form */}
                <div className="bg-white rounded-lg shadow-md p-6">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Story Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Story Description <span className="text-red-500">*</span>
                            </label>
                            <textarea
                                name="storyDescription"
                                value={formData.storyDescription}
                                onChange={handleInputChange}
                                rows={6}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Describe the story you want to generate. Be as detailed as possible for better results..."
                                required
                            />
                            <p className="text-sm text-gray-500 mt-1">
                                Minimum 10 characters. Describe the theme, characters, setting, or any specific elements you want in the story.
                            </p>
                        </div>

                        {/* Form Fields */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Story Length */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Story Length
                                </label>
                                <select
                                    name="storyLength"
                                    value={formData.storyLength}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {storyLengths.map(length => (
                                        <option key={length.value} value={length.value}>
                                            {length.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Genre */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Genre
                                </label>
                                <select
                                    name="genre"
                                    value={formData.genre}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {genres.map(genre => (
                                        <option key={genre.value} value={genre.value}>
                                            {genre.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Writing Style */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Writing Style
                                </label>
                                <select
                                    name="style"
                                    value={formData.style}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {styles.map(style => (
                                        <option key={style.value} value={style.value}>
                                            {style.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Learning Level */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Learning Level
                                </label>
                                <select
                                    name="learningLevel"
                                    value={formData.learningLevel}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    {learningLevels.map(level => (
                                        <option key={level.value} value={level.value}>
                                            {level.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* Settings Summary */}
                        <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Generation Settings Summary:</h4>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                    <span className="font-medium">Length:</span> {storyLengths.find(l => l.value === formData.storyLength)?.label}
                                </div>
                                <div>
                                    <span className="font-medium">Genre:</span> {genres.find(g => g.value === formData.genre)?.label}
                                </div>
                                <div>
                                    <span className="font-medium">Style:</span> {styles.find(s => s.value === formData.style)?.label}
                                </div>
                                <div>
                                    <span className="font-medium">Level:</span> {learningLevels.find(l => l.value === formData.learningLevel)?.label}
                                </div>
                            </div>
                        </div>

                        {/* Submit Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t">
                            <button
                                type="button"
                                onClick={handleGoBack}
                                className="px-6 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition duration-200"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={loading || !formData.storyDescription.trim()}
                                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {loading ? "Generating..." : "Generate Story"}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Note about Implementation */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
                    <h4 className="text-sm font-medium text-yellow-800 mb-1">Note:</h4>
                    <p className="text-yellow-700 text-sm">
                        This is a frontend template for story generation. The actual AI story generation would require backend integration with an AI service (like OpenAI, Anthropic, etc.) and would be implemented in the API endpoints.
                    </p>
                </div>
            </div>

            {loading && <Load />}
        </div>
    )
}

export default AddStoryPage

